import type {ActionResponse, PayloadActionResponse, UUID} from 'src/typings';
import {responseTypes} from 'shared/constants';
import type {ModalWindowAction, ModalWindowResolveValues, ModalWindowSettings, ModalWindowType} from './types';
import type {ModalWindowTypeToModalReturnTypeMap, ModalWindowTypeToModelMap} from './config';
import {MODAL_WINDOW_TYPE_TO_COMPONENT_MAP} from './config';
import {assertIsPayloadActionResponse} from 'shared/utils/types-utils';
import {BehaviorSubject} from 'rxjs';
import {assertUnreachable, uuid} from 'shared/utils/custom-utils';
import {
    createAcknowledgeAction,
    createCancelAction,
    createResolveAction,
    createReturnAction
} from './utils';
import {byId, removeAll} from 'shared/utils/array-utils';
import {
    createAcknowledgeActionResponse,
    createExceptionActionResponse,
    createInterruptActionResponse
} from 'src/modals/modal-utils';
import {getActionResponse} from 'shared/utils/data-service-utils';
import type {SvelteComponentConstructor} from 'core/types';
import type {LogService} from 'core/logging/log.service';
import {updateValue} from 'shared/utils/observables-utils';

type PayloadOrNever<T> = T extends PayloadActionResponse<infer P> ? P : never;

export class ModalDialogService {
    public static readonly serviceName = 'modalDialogService';

    private componentMap = MODAL_WINDOW_TYPE_TO_COMPONENT_MAP;
    private dialogs$: BehaviorSubject<ModalWindowResolveValues<any>[]> = new BehaviorSubject([]);

    /*@ngInject*/
    constructor(private logService: LogService) {
    }

    public getDialog$() {
        return this.dialogs$.asObservable();
    }

    public async openModalWindowAndGetPayload<TYPE extends ModalWindowType>(modalType: TYPE, modalModel: ModalWindowTypeToModelMap[TYPE], modalWindowSettings?: ModalWindowSettings): Promise<PayloadOrNever<ModalWindowTypeToModalReturnTypeMap[TYPE]>> {
        const finishedResponse = await this.openModalWindow(modalType, modalModel, modalWindowSettings);
        assertIsPayloadActionResponse(finishedResponse);
        return finishedResponse.payload;
    }

    public async openModalWindow<TYPE extends ModalWindowType>(modalType: TYPE, modalModel: ModalWindowTypeToModelMap[TYPE], modalWindowSettings?: ModalWindowSettings): Promise<ModalWindowTypeToModalReturnTypeMap[TYPE]> {
        if (!modalType) {
            throw new Error('Trying to create modal window with undefined or null modalType');
        }

        return this.openModalWindowFromComponent<ModalWindowTypeToModelMap[TYPE], ModalWindowTypeToModalReturnTypeMap[TYPE]>(
            this.getModalWindowComponent(modalType),
            modalModel,
            modalWindowSettings,
            modalType
        );
    }

    public async openModalWindowFromComponent<
        MODAL_MODEL extends Record<string, any> | void = void,
        RETURN_TYPE extends ActionResponse | void = void,
    >(component: SvelteComponentConstructor, modalModel: MODAL_MODEL, modalWindowSettings?: ModalWindowSettings, modalType: string = 'custom'): Promise<RETURN_TYPE> {

        const {promise, resolve} = Promise.withResolvers<ModalWindowAction>();
        const id = uuid();
        const resolveValues: ModalWindowResolveValues<any> = {
            id,
            component,
            settings: {size: modalWindowSettings?.size ?? 'md'},
            model: modalModel,
            modalWindowActions: {
                cancel: () => {
                    this.popDialog(id);
                    resolve(createCancelAction());
                },
                acknowledge: () => {
                    this.popDialog(id);
                    resolve(createAcknowledgeAction());
                },
                return: (value: ActionResponse) => {
                    this.popDialog(id);
                    resolve(createReturnAction(value));
                },
                resolve: (value: Promise<ActionResponse>) => {
                    this.popDialog(id);
                    resolve(createResolveAction(value));
                },
                submitPromise: (value: Promise<ActionResponse>) => {
                    this.popDialog(id);
                    resolve(createResolveAction(value));
                }
            }
        };

        this.logService.info(`Opening modal dialog of type: ${modalType ?? 'customComponent'} with id: ${id}`);
        updateValue(this.dialogs$, (dialogs) => [...dialogs, resolveValues]);

        let actionResponse: ActionResponse;
        try {
            const result = await promise;
            actionResponse = await this.resolveModalDialogActionResponse(result);
        } catch (error) {
            const errorMessage = 'Error while resolving modal dialog response';
            this.logService.error(errorMessage, error);
            return Promise.reject(createExceptionActionResponse('ModalDialogError', errorMessage));
        }

        this.logService.info(`Closed modal dialog of type: ${modalType ?? 'customComponent'} with id: ${id}`);
        if (this.isSuccessful(actionResponse)) {
            return Promise.resolve(actionResponse as RETURN_TYPE);
        } else {
            return Promise.reject(actionResponse);
        }
    }

    private popDialog(id: UUID) {
        updateValue(this.dialogs$, (dialogs) => removeAll(dialogs, byId(id)));
    }

    private async resolveModalDialogActionResponse(result: ModalWindowAction): Promise<ActionResponse> {
        switch (result.type) {
            case 'acknowledge':
                return this.modalWindowAcknowledgeHandler();
            case 'cancel':
                return this.modalWindowCancelHandler();
            case 'return':
                return this.modalWindowReturnHandler(result.value);
            case 'resolve':
                return this.modalWindowResolveHandler(result.value);
            default:
                assertUnreachable(result);
        }
    }

    private modalWindowCancelHandler() {
        return createInterruptActionResponse();
    }

    private modalWindowAcknowledgeHandler() {
        return createAcknowledgeActionResponse();
    }

    private modalWindowReturnHandler(response: ActionResponse) {
        return response;
    }

    private async modalWindowResolveHandler(responsePromise: Promise<ActionResponse>) {
        try {
            return await responsePromise;
        } catch (result) {
            return getActionResponse(result);
        }
    }

    private isSuccessful(response: ActionResponse) {
        return response.responseType !== responseTypes.EXCEPTION &&
            response.responseType !== responseTypes.INTERRUPT;
    }

    private getModalWindowComponent(modalType: ModalWindowType): SvelteComponentConstructor {
        return this.componentMap[modalType];
    }
}